import React, { useCallback, useEffect, useRef, useState } from 'react';

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const IconPlay = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M8.2 5.4c-.5-.3-1.2 0-1.2.6v12c0 .6.7.9 1.2.6l10.2-6c.5-.3.5-1 0-1.3l-10.2-6z" />
  </svg>
);

const IconPause = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <rect x="6.5" y="5.5" width="3.5" height="13" rx="1" />
    <rect x="14" y="5.5" width="3.5" height="13" rx="1" />
  </svg>
);

const IconVolume = ({ muted }) =>
  muted ? (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9.5h3.2L11.5 6v12L7.2 14.5H4V9.5z" />
      <path
        d="M15.2 9.3l4.5 4.5M19.7 9.3l-4.5 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9.5h3.2L11.5 6v12L7.2 14.5H4V9.5z" />
      <path
        d="M15 9.2a3.6 3.6 0 010 5.6M17.4 7a6.2 6.2 0 010 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );

const IconFullscreen = ({ exit }) =>
  exit ? (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M9 4H5v4M15 4h4v4M9 20H5v-4M15 20h4v-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M5 9V5h4M15 5h4v4M19 15v4h-4M9 19H5v-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

/**
 * Minimal custom player for uploaded doc videos — cyan/dark DocEditor chrome,
 * hover-reveal controls, no native browser chrome.
 */
export const DocVideoPlayer = ({
  src,
  disabled = false,
  className = '',
}) => {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const hideTimerRef = useRef(null);
  const draggingRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hoverPct, setHoverPct] = useState(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    if (!videoRef.current || videoRef.current.paused || disabled) return;
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 2200);
  }, [disabled]);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => () => clearHideTimer(), []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const onPlay = () => {
      setPlaying(true);
      scheduleHide();
    };
    const onPause = () => {
      setPlaying(false);
      setControlsVisible(true);
      clearHideTimer();
    };
    const onTime = () => {
      if (!draggingRef.current) setCurrent(video.currentTime || 0);
    };
    const onMeta = () => setDuration(video.duration || 0);
    const onDuration = () => setDuration(video.duration || 0);
    const onProgress = () => {
      try {
        if (!video.buffered?.length) return;
        setBuffered(video.buffered.end(video.buffered.length - 1));
      } catch {
        /* ignore */
      }
    };
    const onVolume = () => setMuted(video.muted || video.volume === 0);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('durationchange', onDuration);
    video.addEventListener('progress', onProgress);
    video.addEventListener('volumechange', onVolume);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('durationchange', onDuration);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('volumechange', onVolume);
    };
  }, [scheduleHide, src]);

  useEffect(() => {
    const onFs = () => {
      const el = rootRef.current;
      setIsFullscreen(!!(document.fullscreenElement && el && document.fullscreenElement === el));
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const togglePlay = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (disabled) return;
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
      revealControls();
    },
    [disabled, revealControls]
  );

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    if (!video || disabled) return;
    video.muted = !video.muted;
    setMuted(video.muted);
    revealControls();
  };

  const toggleFullscreen = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const root = rootRef.current;
    if (!root || disabled) return;
    try {
      if (document.fullscreenElement === root) {
        await document.exitFullscreen();
      } else {
        await root.requestFullscreen();
      }
    } catch {
      /* ignore */
    }
    revealControls();
  };

  const seekToClientX = (clientX, trackEl) => {
    const video = videoRef.current;
    if (!video || !trackEl || !duration) return;
    const rect = trackEl.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const next = pct * duration;
    video.currentTime = next;
    setCurrent(next);
  };

  const onScrubPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !duration) return;
    const track = e.currentTarget;
    draggingRef.current = true;
    seekToClientX(e.clientX, track);
    track.setPointerCapture?.(e.pointerId);

    const onMove = (ev) => seekToClientX(ev.clientX, track);
    const onUp = (ev) => {
      draggingRef.current = false;
      seekToClientX(ev.clientX, track);
      track.releasePointerCapture?.(ev.pointerId);
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onUp);
      revealControls();
    };
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
  };

  const progressPct = duration > 0 ? (current / duration) * 100 : 0;
  const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0;
  const showChrome = !disabled && (controlsVisible || !playing);

  const stopBubble = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={rootRef}
      className={[
        'doc-video-player',
        showChrome ? 'doc-video-player--chrome' : '',
        playing ? 'doc-video-player--playing' : 'doc-video-player--paused',
        disabled ? 'doc-video-player--disabled' : '',
        isFullscreen ? 'doc-video-player--fullscreen' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseMove={() => {
        if (!disabled) revealControls();
      }}
      onMouseLeave={() => {
        if (playing && !disabled) {
          clearHideTimer();
          setControlsVisible(false);
        }
      }}
    >
      <video
        ref={videoRef}
        className="doc-video-player__video"
        src={src}
        playsInline
        preload="metadata"
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
      />

      {!disabled && !playing && (
        <button
          type="button"
          className="doc-video-player__big-play"
          aria-label="Play"
          onPointerDown={stopBubble}
          onMouseDown={stopBubble}
          onClick={togglePlay}
        >
          <IconPlay />
        </button>
      )}

      <div
        className="doc-video-player__bar"
        onPointerDown={stopBubble}
        onMouseDown={stopBubble}
        onClick={stopBubble}
      >
        <div className="doc-video-player__cluster">
          <button
            type="button"
            className="doc-video-player__btn doc-video-player__btn--play"
            aria-label={playing ? 'Pause' : 'Play'}
            onClick={togglePlay}
          >
            {playing ? <IconPause /> : <IconPlay />}
          </button>
        </div>

        <div className="doc-video-player__scrub-wrap">
          <div className="doc-video-player__time">
            <strong>{formatTime(current)}</strong>
            {' · '}
            {formatTime(duration)}
          </div>
          <div
            className="doc-video-player__scrub"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration || 0)}
            aria-valuenow={Math.round(current || 0)}
            tabIndex={disabled ? -1 : 0}
            onPointerDown={onScrubPointerDown}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              if (!rect.width) return;
              setHoverPct(((e.clientX - rect.left) / rect.width) * 100);
            }}
            onMouseLeave={() => setHoverPct(null)}
            onKeyDown={(e) => {
              const video = videoRef.current;
              if (!video || disabled) return;
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                video.currentTime = Math.max(0, video.currentTime - 5);
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                video.currentTime = Math.min(duration, video.currentTime + 5);
              }
            }}
          >
            <div className="doc-video-player__scrub-track">
              <div
                className="doc-video-player__scrub-buffered"
                style={{ width: `${bufferedPct}%` }}
              />
              <div
                className="doc-video-player__scrub-played"
                style={{ width: `${progressPct}%` }}
              />
              {hoverPct != null && (
                <div
                  className="doc-video-player__scrub-hover"
                  style={{ width: `${hoverPct}%` }}
                />
              )}
              <div
                className="doc-video-player__scrub-thumb"
                style={{ left: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        <div className="doc-video-player__cluster">
          <button
            type="button"
            className="doc-video-player__btn"
            aria-label={muted ? 'Unmute' : 'Mute'}
            onClick={toggleMute}
          >
            <IconVolume muted={muted} />
          </button>
          <button
            type="button"
            className="doc-video-player__btn"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={toggleFullscreen}
          >
            <IconFullscreen exit={isFullscreen} />
          </button>
        </div>
      </div>
    </div>
  );
};
